import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import HotelsManagement from "../components/HotelsManagement";
import CashReport from "../components/CashReport";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { AdminLogoutButton } from "../components/AdminGate";
import { Calendar as CalendarIcon, Plus, Search, Trash2, CheckCircle2, XCircle, LogIn, LogOut, RefreshCw, Printer, MessageCircle, ListPlus } from "lucide-react";
import { whatsappReceiptUrl } from "../lib/whatsapp";
import { format, differenceInCalendarDays } from "date-fns";
import { computeInvoice, cleanExtras } from "../lib/invoice";
import { ExtrasEditor } from "../components/ExtrasEditor";
import { ExtrasDialog } from "../components/ExtrasDialog";

const emptyForm = {
  hotel_id: "",
  guest_first_name: "",
  guest_last_name: "",
  phone: "",
  email: "",
  check_in: undefined,
  check_out: undefined,
  room_type: "",
  guests: 2,
  rooms: 1,
  rate_per_night: "",
  discount: "",
  gst_rate: "12",
  extras: [],
  currency: "INR",
  payment_method: "cash",
  payment_status: "paid",
  notes: "",
};

const statusColors = {
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  checked_in: "bg-blue-100 text-blue-800 border-blue-200",
  checked_out: "bg-neutral-200 text-neutral-800 border-neutral-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

const AdminBookings = () => {
  const { toast } = useToast();
  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [extrasFor, setExtrasFor] = useState(null);

  const loadHotels = async () => {
    try {
      const [h, r] = await Promise.all([api.listHotels(), api.listRoomTypes()]);
      setHotels(h);
      setRoomTypes(r);
    } catch (e) {
      toast({ title: "Failed to load hotels", description: e.message });
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const data = await api.listBookings(params);
      setBookings(data);
    } catch (e) {
      toast({ title: "Failed to load bookings", description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHotels();
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(loadBookings, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const roomsFor = (hotelId) => {
    const h = hotels.find((x) => x.id === hotelId);
    const pk = (h?.room_packages || []).map((p) => p.room_type);
    return pk.length ? pk : h?.room_types?.length ? h.room_types : roomTypes;
  };

  const pickRoom = (hotelId, roomType) => {
    const h = hotels.find((x) => x.id === hotelId);
    const room = roomsFor(hotelId).includes(roomType) ? roomType : "";
    const pkg = (h?.room_packages || []).find((p) => p.room_type === room);
    const rate = pkg?.price ?? (room ? h?.price_per_night : undefined);
    setForm((f) => ({ ...f, hotel_id: hotelId, room_type: room, rate_per_night: rate != null ? String(rate) : room ? f.rate_per_night : "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hotel_id || !form.guest_first_name || !form.guest_last_name || !form.phone || !form.check_in || !form.check_out || !form.room_type || !form.rate_per_night) {
      toast({ title: "Missing fields", description: "Please fill all required fields." });
      return;
    }
    if (form.extras.some((x) => !String(x.name || "").trim() && x.price !== "")) {
      toast({ title: "Extra charge needs a name", description: "Name each extra charge, or remove the empty row." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        check_in: format(form.check_in, "yyyy-MM-dd"),
        check_out: format(form.check_out, "yyyy-MM-dd"),
        rate_per_night: parseFloat(form.rate_per_night),
        discount: parseFloat(form.discount) || 0,
        gst_rate: parseFloat(form.gst_rate) || 0,
        extras: cleanExtras(form.extras),
        guests: parseInt(form.guests) || 1,
        rooms: parseInt(form.rooms) || 1,
        email: form.email || undefined,
      };
      const created = await api.createBooking(payload);
      toast({ title: "Booking confirmed", description: `Booking ID: ${created.booking_id}` });
      setForm(emptyForm);
      setOpen(false);
      loadBookings();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast({ title: "Could not create booking", description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (bid, s) => {
    try {
      await api.updateStatus(bid, s);
      toast({ title: "Status updated", description: `${bid} → ${s}` });
      loadBookings();
    } catch (e) {
      toast({ title: "Update failed", description: e.message });
    }
  };

  const remove = async (bid) => {
    if (!window.confirm(`Delete booking ${bid}?`)) return;
    try {
      await api.deleteBooking(bid);
      toast({ title: "Booking deleted" });
      loadBookings();
    } catch (e) {
      toast({ title: "Delete failed", description: e.message });
    }
  };

  const total = bookings.length;
  const cashCollected = bookings
    .filter((b) => b.payment_status === "paid" && b.payment_method === "cash")
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const confirmedCount = bookings.filter((b) => b.booking_status === "confirmed").length;
  const checkedInCount = bookings.filter((b) => b.booking_status === "checked_in").length;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <Header />
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Internal · Reservations Desk</span>
              <h1 className="text-3xl md:text-4xl font-serif font-medium mt-2">Reservations Desk</h1>
              <p className="text-sm text-neutral-600 mt-1">Manage bookings, cash collections and the hotels shown on the public site.</p>
            </div>
            <div className="flex items-center gap-3">
              <AdminLogoutButton />
            </div>
          </div>

          <Tabs defaultValue="bookings" className="w-full">
            <TabsList className="bg-transparent border-b border-neutral-200 w-full justify-start rounded-none h-auto p-0 mb-8 gap-8">
              <TabsTrigger value="bookings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#c8102e] data-[state=active]:text-[#c8102e] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm font-medium text-neutral-600 hover:text-neutral-900">
                Bookings
              </TabsTrigger>
              <TabsTrigger value="hotels" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#c8102e] data-[state=active]:text-[#c8102e] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm font-medium text-neutral-600 hover:text-neutral-900">
                Hotels
              </TabsTrigger>
              <TabsTrigger value="reports" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#c8102e] data-[state=active]:text-[#c8102e] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm font-medium text-neutral-600 hover:text-neutral-900">
                Cash Report
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-0">
              <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-5 py-5">
                  <Plus size={16} className="mr-2" /> New Booking
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Create New Booking</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="md:col-span-2">
                    <Label>Hotel *</Label>
                    <Select value={form.hotel_id} onValueChange={(v) => pickRoom(v, form.room_type)}>
                      <SelectTrigger data-testid="booking-hotel-select"><SelectValue placeholder="Select hotel" /></SelectTrigger>
                      <SelectContent>
                        {hotels.map((h) => (
                          <SelectItem key={h.id} value={h.id}>{h.name} — {h.city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>First name *</Label>
                    <Input data-testid="booking-first-name-input" value={form.guest_first_name} onChange={(e) => setField("guest_first_name", e.target.value)} />
                  </div>
                  <div>
                    <Label>Last name *</Label>
                    <Input data-testid="booking-last-name-input" value={form.guest_last_name} onChange={(e) => setField("guest_last_name", e.target.value)} />
                  </div>

                  <div>
                    <Label>Phone *</Label>
                    <Input data-testid="booking-phone-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+91 98706 27398" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="guest@example.com" />
                  </div>

                  <div>
                    <Label>Check-in *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal rounded-none">
                          <CalendarIcon size={14} className="mr-2" />
                          {form.check_in ? format(form.check_in, "d MMM yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.check_in} onSelect={(d) => setField("check_in", d)} initialFocus /></PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Check-out *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal rounded-none">
                          <CalendarIcon size={14} className="mr-2" />
                          {form.check_out ? format(form.check_out, "d MMM yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.check_out} onSelect={(d) => setField("check_out", d)} initialFocus /></PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label>Room type *</Label>
                    <Select value={form.room_type} onValueChange={(v) => pickRoom(form.hotel_id, v)}>
                      <SelectTrigger data-testid="booking-room-select"><SelectValue placeholder="Select room" /></SelectTrigger>
                      <SelectContent>
                        {roomsFor(form.hotel_id).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Guests</Label>
                      <Input type="number" min="1" value={form.guests} onChange={(e) => setField("guests", e.target.value)} />
                    </div>
                    <div>
                      <Label>Rooms</Label>
                      <Input type="number" min="1" value={form.rooms} onChange={(e) => setField("rooms", e.target.value)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 md:col-span-2">
                    <div className="col-span-2">
                      <Label>Rate per room / night *</Label>
                      <Input data-testid="booking-rate-input" type="number" min="0" step="0.01" value={form.rate_per_night} onChange={(e) => setField("rate_per_night", e.target.value)} placeholder="12500" />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={form.currency} onValueChange={(v) => setField("currency", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="AED">AED</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 md:col-span-2">
                    <div>
                      <Label>Discount (₹)</Label>
                      <Input data-testid="booking-discount-input" type="number" min="0" step="0.01" value={form.discount} onChange={(e) => setField("discount", e.target.value)} placeholder="0" />
                    </div>
                    <div>
                      <Label>GST %</Label>
                      <Select value={form.gst_rate} onValueChange={(v) => setField("gst_rate", v)}>
                        <SelectTrigger data-testid="booking-gst-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["0", "5", "12", "18"].map((g) => <SelectItem key={g} value={g}>{g}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <ExtrasEditor value={form.extras} onChange={(v) => setField("extras", v)} />
                  </div>
                  <BillPreview form={form} />

                  <div>
                    <Label>Payment method</Label>
                    <Select value={form.payment_method} onValueChange={(v) => setField("payment_method", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card (POS)</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment status</Label>
                    <Select value={form.payment_status} onValueChange={(v) => setField("payment_status", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid (collected)</SelectItem>
                        <SelectItem value="pending">Pending (pay at hotel)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Late check-in requested, airport pickup, etc." />
                  </div>

                  <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                    <Button data-testid="booking-submit-btn" type="submit" disabled={submitting} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-6">
                      {submitting ? "Saving..." : "Confirm Booking"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
              </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total bookings", value: total },
              { label: "Confirmed", value: confirmedCount },
              { label: "Checked-in", value: checkedInCount },
              { label: "Cash collected", value: `₹ ${cashCollected.toLocaleString("en-IN")}` },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-neutral-200 p-5">
                <div className="text-xs uppercase tracking-widest text-neutral-500">{s.label}</div>
                <div className="text-2xl font-serif font-medium mt-2">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white border border-neutral-200 p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1 flex items-center gap-2 border border-neutral-200 px-3">
              <Search size={16} className="text-neutral-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by booking ID, guest, phone, hotel..."
                className="flex-1 py-2 outline-none text-sm"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="checked_in">Checked-in</SelectItem>
                <SelectItem value="checked_out">Checked-out</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadBookings} className="rounded-none">
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          {/* Table */}
          <div className="bg-white border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 text-neutral-700">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Booking ID</th>
                  <th className="px-4 py-3 font-medium">Guest</th>
                  <th className="px-4 py-3 font-medium">Hotel</th>
                  <th className="px-4 py-3 font-medium">Stay</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Payment</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-neutral-500">No bookings yet. Click "New Booking" to add one.</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{b.booking_id}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{b.guest_first_name} {b.guest_last_name}</div>
                      <div className="text-xs text-neutral-500">{b.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{b.hotel_name}</div>
                      <div className="text-xs text-neutral-500">{b.room_type} · {b.rooms} room · {b.guests} guests</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{b.check_in} → {b.check_out}</div>
                      <div className="text-xs text-neutral-500">{b.nights} night{b.nights > 1 ? "s" : ""}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.currency} {Number(b.amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <div className="capitalize">{b.payment_method}</div>
                      <Badge variant="outline" className={`mt-1 rounded-none text-[10px] ${b.payment_status === "paid" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                        {b.payment_status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`rounded-none capitalize ${statusColors[b.booking_status]}`}>
                        {b.booking_status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <a title="Cash receipt" href={`/receipt/${b.booking_id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-neutral-100 text-neutral-700 inline-flex"><Printer size={15} /></a>
                        <a data-testid={`whatsapp-receipt-${b.booking_id}`} title="Send receipt on WhatsApp" href={whatsappReceiptUrl(b)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-emerald-50 text-emerald-700 inline-flex"><MessageCircle size={15} /></a>
                        {b.rate_per_night != null && (
                          <button data-testid={`extras-btn-${b.booking_id}`} title="Extra charges" onClick={() => setExtrasFor(b)} className="p-1.5 hover:bg-neutral-100 text-neutral-700"><ListPlus size={15} /></button>
                        )}
                        {b.booking_status === "confirmed" && (
                          <button title="Check-in" onClick={() => changeStatus(b.booking_id, "checked_in")} className="p-1.5 hover:bg-blue-50 text-blue-700"><LogIn size={15} /></button>
                        )}
                        {b.booking_status === "checked_in" && (
                          <button title="Check-out" onClick={() => changeStatus(b.booking_id, "checked_out")} className="p-1.5 hover:bg-neutral-100 text-neutral-700"><LogOut size={15} /></button>
                        )}
                        {b.booking_status !== "cancelled" && b.booking_status !== "checked_out" && (
                          <button title="Cancel" onClick={() => changeStatus(b.booking_id, "cancelled")} className="p-1.5 hover:bg-rose-50 text-rose-700"><XCircle size={15} /></button>
                        )}
                        {b.booking_status === "cancelled" && (
                          <button title="Reconfirm" onClick={() => changeStatus(b.booking_id, "confirmed")} className="p-1.5 hover:bg-emerald-50 text-emerald-700"><CheckCircle2 size={15} /></button>
                        )}
                        <button title="Delete" onClick={() => remove(b.booking_id)} className="p-1.5 hover:bg-rose-50 text-rose-700"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            </TabsContent>

            <TabsContent value="hotels" className="mt-0">
              <HotelsManagement />
            </TabsContent>

            <TabsContent value="reports" className="mt-0">
              <CashReport />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
      {extrasFor && <ExtrasDialog booking={extrasFor} onClose={() => setExtrasFor(null)} onSaved={loadBookings} />}
    </div>
  );
};

const BillPreview = ({ form }) => {
  const nights = form.check_in && form.check_out ? differenceInCalendarDays(form.check_out, form.check_in) : 0;
  if (!form.rate_per_night || nights < 1) return null;
  const inv = computeInvoice({ rate: form.rate_per_night, nights, rooms: form.rooms, discount: form.discount, gstRate: form.gst_rate, extras: form.extras });
  const f = (n) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div data-testid="booking-bill-preview" className="md:col-span-2 border border-neutral-200 bg-neutral-50 p-3 text-xs grid grid-cols-2 gap-x-6 gap-y-1">
      <span>{inv.nights} night(s) × {inv.rooms} room(s)</span><span className="text-right">{f(inv.roomTotal)}</span>
      {inv.items.length > 0 && <><span>Extra charges ({inv.items.length})</span><span className="text-right">{f(inv.extrasTotal)}</span></>}
      <span>Discount</span><span className="text-right">− {f(inv.discount)}</span>
      <span>GST @ {inv.gstRate}%</span><span className="text-right">{f(inv.gst)}</span>
      <span>Round off</span><span className="text-right">{f(inv.roundOff)}</span>
      <span className="font-semibold">Total payable</span><span data-testid="booking-bill-total" className="text-right font-semibold">{form.currency} {f(inv.total)}</span>
    </div>
  );
};

export default AdminBookings;
